import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import SanityImage from 'gatsby-plugin-sanity-image';
import slugify from 'slugify';

import styles from '@repo/ui-components/styles';
import breakpoints from '@repo/ui-components/breakpoints';

const Fieldset = styled.fieldset`
  border: 0;
  padding: 0;
  margin: 0;

  h3 {
    margin-bottom: ${styles.spacing['spacing-lg']};
  }
`;

const AnswersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${styles.spacing['spacing-lg']};
  align-items: center;

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
    align-items: start;
  }
`;

const QuestionLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${styles.spacing['spacing-md']};
  cursor: pointer;
  border-radius: ${styles.spacing['spacing-2xs']};
  width: 100%;
  min-height: 180px;
  padding: ${styles.spacing['spacing-sm']};

  ${({ theme, isSelected }) =>
    isSelected
      ? {
          backgroundColor: theme.button.primary.backgroundActive,
          border: theme.button.primary.borderColor,
          color: theme.button.primary.textColor,
        }
      : {
          backgroundColor: theme.button.primary.background,
          border: theme.button.primary.borderColor,
          color: theme.button.primary.textColor,
        }}

  :hover {
    background-color: ${({ theme }) => theme.button.primary.backgroundHover};
  }

  :focus {
    border: ${({ theme }) => theme.button.primary.backgroundHover};
  }

  input {
    cursor: pointer;
  }

  small {
    display: block;
  }

  svg {
    fill: ${({ theme }) => theme.button.primary.textColor};
  }
`;

const StyledImage = styled(SanityImage)`
  display: block;
  width: 100%;
  max-width: 100px;
  padding: ${styles.spacing['spacing-1xs']};
`;

const AnswerContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: ${styles.spacing['spacing-md']};
`;

const RadioContainer = styled.div`
  display: flex;
  gap: ${styles.spacing['spacing-md']};

  input[type='radio'] {
    display: none;
  }
`;

const AnswerTextContainer = styled.div`
  text-align: center;

  p {
    color: ${({ theme }) => theme.button.primary.textColor};
    margin: 0;
  }
`;

function Questions({ card, answers, currentSection, handleAnswerChange }) {
  return (
    <Fieldset>
      {card?.headline && (
        <legend>
          <h3>{card.headline}</h3>
        </legend>
      )}
      <AnswersContainer>
        {card?.answers?.map(answer => {
          const answerCardId = slugify(answer.title, {
            strict: true,
            lower: true,
          });

          return (
            <QuestionLabel
              key={answerCardId}
              id={`card_label_${answerCardId}`}
              htmlFor={`card_input_${answerCardId}`}
              isSelected={answers?.[currentSection]?.answer === answer?.title}
              onKeyDown={e => {
                if (['Enter', 'Space'].includes(e.code)) {
                  const answerEvent = {
                    target: {
                      value: answer.title,
                    },
                  };
                  handleAnswerChange(answerEvent, answer._key, answer);
                }
              }}
              tabIndex={0}
            >
              <AnswerContainer>
                <RadioContainer
                  role="radio"
                  aria-labelledby={`card_label_${answerCardId}`}
                  aria-checked={
                    answers?.[currentSection]?.answer === answer?.title
                  }
                >
                  <input
                    type="radio"
                    id={`card_input_${answerCardId}`}
                    name={`card_input_${answerCardId}`}
                    value={answer.title}
                    checked={
                      answers?.[currentSection]?.answer === answer?.title
                    }
                    onChange={e => handleAnswerChange(e, answer._key, answer)}
                  />
                </RadioContainer>
                {answer?.image?.asset && (
                  <StyledImage
                    asset={answer.image.asset}
                    hotspot={answer.image?.hotspot}
                    crop={answer.image?.crop}
                  />
                )}
                <AnswerTextContainer>
                  <p>{answer.title}</p>
                  {answer?.helptext && <small>{answer.helptext}</small>}
                </AnswerTextContainer>
              </AnswerContainer>
            </QuestionLabel>
          );
        })}
      </AnswersContainer>
    </Fieldset>
  );
}

Questions.propTypes = {
  card: PropTypes.shape({
    _key: PropTypes.string,
    headline: PropTypes.string,
    answers: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  answers: PropTypes.arrayOf(
    PropTypes.shape({
      answer: PropTypes.string,
    })
  ),
  currentSection: PropTypes.number,
  handleAnswerChange: PropTypes.func,
};

Questions.defaultProps = {
  card: {},
  answers: [],
  currentSection: 0,
  handleAnswerChange: () => {},
};

export default Questions;
